"use client";

import { useEffect, useState } from "react";
import { getCurrentUser } from "@/lib/auth";

type AuthState = "loading" | "allowed" | "denied";

type Props = {
    entityType: string;
    entityId: string | number;
    children: React.ReactNode;
};

function MembersOnlyPlaceholder() {
    return (
        <section style={{ backgroundColor: "#003d45" }} className="border-b-8 text-white py-20 relative overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                <div
                    className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                    style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
                >
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1.5"
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                    </svg>
                </div>
                <h2 className="text-2xl font-bold mb-3 text-white">Volunteers &amp; Leadership Only</h2>
                <p className="max-w-md mx-auto text-base leading-relaxed" style={{ color: "rgba(255,255,255,0.75)" }}>
                    The Governance Charter is available to group volunteers, staff, and administrators.
                    Sign in with your volunteer credentials to view.
                </p>
            </div>
        </section>
    );
}

/**
 * Gates the governance section to members/staff/admins only.
 * Auth is checked client-side (JWT is in localStorage, not a cookie).
 *
 * Access is granted if:
 *  - System role is admin, superadmin, or staff
 *  - OR the user has any assignment for this entity (any role — chair, member, liaison, etc.)
 *
 * Starts in "loading" state (renders nothing) to prevent any flash of ungated content.
 */
export default function GovernanceGate({ entityType, entityId, children }: Props) {
    const [authState, setAuthState] = useState<AuthState>("loading");

    useEffect(() => {
        let cancelled = false;

        async function check() {
            try {
                const profile = await getCurrentUser();
                if (cancelled) return;

                if (!profile) {
                    setAuthState("denied");
                    return;
                }

                const role = (profile.role || "").toLowerCase();
                if (role === "admin" || role === "superadmin" || role === "staff") {
                    setAuthState("allowed");
                    return;
                }

                const assignments: Array<{ entity_id?: string | number; entity_type?: string }> =
                    profile.assignments || [];

                const hasAssignment = assignments.some(
                    (a) =>
                        String(a.entity_id) === String(entityId) &&
                        (a.entity_type || "").toLowerCase() === String(entityType).toLowerCase()
                );

                setAuthState(hasAssignment ? "allowed" : "denied");
            } catch {
                if (!cancelled) setAuthState("denied");
            }
        }

        check();
        return () => {
            cancelled = true;
        };
    }, [entityType, entityId]);

    if (authState === "loading") return null;
    if (authState === "denied") return <MembersOnlyPlaceholder />;
    return <>{children}</>;
}
